'use client';

import Link from 'next/link';
import { ChevronLeft } from 'lucide-react';

export default function PlatformPrivacyPolicyPage() {
  return (
    <div className="bg-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100 py-4">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-4">
              <li>
                <Link href="/docs" className="text-brand hover:text-[#2a6bb0] font-medium">
                  Docs & Guides
                </Link>
              </li>
              <li>
                <div className="flex items-center">
                  <svg className="shrink-0 h-5 w-5 text-gray-300" xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
                  </svg>
                  <span className="ml-4 text-sm font-medium text-gray-600">Privacy Policy</span>
                </div>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="lg:grid lg:grid-cols-4 lg:gap-12">

          {/* Table of Contents - Sidebar */}
          <div className="hidden lg:block lg:col-span-1">
            <div className="sticky top-8">
              <h3 className="text-sm font-semibold tracking-wide uppercase text-gray-400 mb-4">On this page</h3>
              <nav className="space-y-3 border-l-2 border-gray-100 pl-4">
                <a href="#introduction" className="block text-sm text-gray-600 hover:text-brand transition-colors">Introduction</a>
                <a href="#information-collected" className="block text-sm text-gray-600 hover:text-brand transition-colors">Information We Collect</a>
                <a href="#how-we-use" className="block text-sm text-gray-600 hover:text-brand transition-colors">How We Use Information</a>
                <a href="#sharing" className="block text-sm text-gray-600 hover:text-brand transition-colors">How We Share Information</a>
                <a href="#sms-data" className="block text-sm text-gray-600 hover:text-brand transition-colors">SMS Messaging Data</a>
                <a href="#retention" className="block text-sm text-gray-600 hover:text-brand transition-colors">Retention & Deletion</a>
                <a href="#security" className="block text-sm text-gray-600 hover:text-brand transition-colors">Security</a>
                <a href="#your-rights" className="block text-sm text-gray-600 hover:text-brand transition-colors">Your Rights</a>
                <a href="#agency-responsibilities" className="block text-sm text-gray-600 hover:text-brand transition-colors">Agency Responsibilities</a>
                <a href="#children" className="block text-sm text-gray-600 hover:text-brand transition-colors">Children&apos;s Privacy</a>
                <a href="#changes" className="block text-sm text-gray-600 hover:text-brand transition-colors">Changes</a>
                <a href="#contact" className="block text-sm text-gray-600 hover:text-brand transition-colors">Contact</a>
                <a href="#ccpa" className="block text-sm text-gray-600 hover:text-brand transition-colors">CCPA Supplement</a>
                <a href="#gdpr" className="block text-sm text-gray-600 hover:text-brand transition-colors">GDPR Supplement</a>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {/* Header */}
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
              <p className="text-xl text-gray-600">
                How Voley collects, uses, and protects information when you use our platform.
              </p>
              <div className="mt-4 flex items-center text-sm text-gray-500">
                <span>Effective Date: [EFFECTIVE_DATE]</span>
                <span className="mx-3">|</span>
                <span>Last Updated: [LAST_UPDATED_DATE]</span>
              </div>
            </div>

            {/* Content Sections */}
            <div className="prose prose-lg max-w-none">

              {/* 1. Introduction */}
              <section id="introduction" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">1. Introduction</h2>
                <p className="text-gray-600 mb-4">
                  Voley (&quot;Voley,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates a business messaging platform at <a href="https://letsvoley.com" className="text-brand hover:text-[#2a6bb0]">letsvoley.com</a> (the &quot;Platform&quot;). Our Platform enables independent insurance agencies and other businesses (&quot;Customers&quot; or &quot;Agencies&quot;) to communicate with their clients and prospects (&quot;End Users&quot;) via SMS text messaging and related channels.
                </p>
                <p className="text-gray-600 mb-4">
                  This Privacy Policy describes how we collect, use, disclose, and protect information in connection with our Platform, including information about our Customers, their authorized users, and the End Users they communicate with.
                </p>

                <div className="bg-brand/10 border border-brand/20 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <h4 className="font-semibold text-gray-900 mb-2">Our Role</h4>
                  <p className="text-sm text-gray-700 mb-3">
                    <strong>For End User data:</strong> Voley acts as a <strong>Service Provider / Data Processor</strong>. Our Customers (Agencies) are the data controllers who determine the purposes and means of processing End User personal information. We process End User data only on behalf of, and according to the instructions of, our Customers.
                  </p>
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>For Customer data:</strong> Voley acts as a <strong>Data Controller</strong> for the account, billing, and usage information we collect directly from our Customers in order to provide the Platform.
                  </p>
                </div>
              </section>

              {/* 2. Information We Collect */}
              <section id="information-collected" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">2. Information We Collect</h2>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Information Provided by Customers</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Account information:</strong> Business name, address, EIN, website URL, contact name, email address, and phone number</li>
                  <li><strong>User credentials:</strong> Names, email addresses, and authentication data for authorized team members</li>
                  <li><strong>Billing information:</strong> Payment method details (processed by our payment processor; we do not store full payment card numbers)</li>
                  <li><strong>Integration data:</strong> Credentials and configuration for third-party integrations (e.g., HawkSoft)</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">End User Information (Processed on Behalf of Customers)</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Contact details:</strong> Phone numbers, names, and other contact identifiers provided by the Customer or the End User</li>
                  <li><strong>Message content:</strong> The content of SMS messages sent and received through the Platform</li>
                  <li><strong>Message metadata:</strong> Timestamps (sent, delivered, read), delivery status, and message direction</li>
                  <li><strong>Consent and opt-out records:</strong> Records of End User opt-in, opt-out, and HELP requests</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Information Collected Automatically</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Device and browser data:</strong> IP address, browser type, operating system, and device identifiers when Customers access the Platform</li>
                  <li><strong>Usage analytics:</strong> Pages viewed, features used, session duration, and interaction patterns within the Platform</li>
                  <li><strong>Log data:</strong> Server logs including access times, error logs, and API call records</li>
                  <li><strong>Cookies and similar technologies:</strong> Session cookies, authentication tokens, and analytics identifiers (see our Cookie section below)</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Cookies</h3>
                <p className="text-gray-600 mb-4">
                  We use cookies and similar technologies for authentication, security, preferences, and analytics. You can control cookies through your browser settings, though disabling certain cookies may impair Platform functionality.
                </p>
              </section>

              {/* 3. How We Use Information */}
              <section id="how-we-use" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">3. How We Use Information</h2>
                <p className="text-gray-600 mb-4">
                  We use the information we collect for the following purposes:
                </p>

                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Provide and operate the Platform:</strong> Route and deliver messages, manage conversations, sync contacts, and enable collaboration features</li>
                  <li><strong>Customer support:</strong> Respond to inquiries, troubleshoot issues, and provide technical assistance</li>
                  <li><strong>Compliance and legal obligations:</strong> Process opt-out and HELP requests, maintain message logs for regulatory compliance, support A2P 10DLC registration, and respond to lawful requests</li>
                  <li><strong>Security and fraud prevention:</strong> Detect and prevent unauthorized access, abuse, spam, and other harmful activity</li>
                  <li><strong>Billing and account management:</strong> Process payments, manage subscriptions, and send account-related communications</li>
                  <li><strong>Platform improvement:</strong> Analyze usage patterns to improve reliability, performance, and user experience</li>
                  <li><strong>Onboarding and registration:</strong> Submit carrier registration (A2P 10DLC) on behalf of Customers through Twilio</li>
                </ul>

                <div className="bg-brand-green/15 border border-brand-green/40 rounded-2xl p-5 mb-6 [&>:first-child]:mt-0">
                  <p className="text-sm text-gray-700 mb-0">
                    <strong>We do not sell personal information.</strong> We do not sell, rent, or trade the personal information of our Customers or their End Users to third parties for their own marketing purposes.
                  </p>
                </div>
              </section>

              {/* 4. How We Share Information */}
              <section id="sharing" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">4. How We Share Information</h2>
                <p className="text-gray-600 mb-4">
                  We share information only in the following circumstances:
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Service Providers and Subprocessors</h3>
                <p className="text-gray-600 mb-4">
                  We use trusted third-party service providers to help us operate the Platform. These providers process data only on our behalf and are contractually obligated to protect it. Key subprocessors include:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Twilio:</strong> Telecommunications provider for SMS message routing and delivery, phone number provisioning, and A2P 10DLC registration</li>
                  <li><strong>Cloud infrastructure providers:</strong> Hosting, storage, and database services</li>
                  <li><strong>Payment processors:</strong> Secure payment processing for billing</li>
                  <li><strong>Analytics providers:</strong> Platform usage analytics and error monitoring</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Legal and Regulatory Disclosures</h3>
                <p className="text-gray-600 mb-4">
                  We may disclose information when we believe in good faith that disclosure is necessary to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Comply with applicable law, regulation, legal process, or governmental request</li>
                  <li>Enforce our Terms of Service or other agreements</li>
                  <li>Protect the rights, property, or safety of Voley, our Customers, or others</li>
                  <li>Detect, prevent, or address fraud, security, or technical issues</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Business Transfers</h3>
                <p className="text-gray-600 mb-4">
                  If Voley is involved in a merger, acquisition, or sale of assets, personal information may be transferred as part of that transaction. We will notify affected parties of any change in ownership or use of personal information.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">With Customer&apos;s Consent</h3>
                <p className="text-gray-600 mb-4">
                  We may share information with third parties when a Customer has directed or authorized such sharing (for example, enabling an integration with a third-party service).
                </p>
              </section>

              {/* 5. SMS Messaging Data */}
              <section id="sms-data" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">5. SMS Messaging Data</h2>
                <p className="text-gray-600 mb-4">
                  Because our Platform is centered on SMS communication, we want to be transparent about how messaging data is handled:
                </p>

                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Message routing:</strong> Message content is transmitted to and from End Users through Twilio&apos;s telecommunications network. Twilio processes message data as a subprocessor to deliver messages to mobile carriers.</li>
                  <li><strong>Message storage:</strong> Message content and metadata are stored on our Platform so Customers can view conversation history and maintain records.</li>
                  <li><strong>Opt-out processing:</strong> We automatically detect opt-out keywords (STOP, UNSUBSCRIBE, etc.) and update contact records to prevent further messaging. Opt-out status is shared with the Customer.</li>
                  <li><strong>HELP responses:</strong> We process HELP keyword requests and provide the Customer&apos;s support contact information to the End User.</li>
                  <li><strong>No message content selling:</strong> We do not sell, share for advertising purposes, or use message content for any purpose other than providing the Platform service to the Customer.</li>
                </ul>
              </section>

              {/* 6. Retention & Deletion */}
              <section id="retention" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">6. Data Retention and Deletion</h2>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Retention Periods</h3>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Account data:</strong> Retained for the duration of the Customer&apos;s active account, plus a reasonable period afterward for administrative purposes</li>
                  <li><strong>Message data:</strong> Retained in accordance with the Customer&apos;s account settings and applicable regulatory requirements. Customers may configure retention periods within the Platform.</li>
                  <li><strong>Opt-out records:</strong> Retained indefinitely to ensure continued compliance with opt-out requests</li>
                  <li><strong>Billing records:</strong> Retained as required by applicable tax and financial regulations</li>
                  <li><strong>Log data:</strong> Retained for a reasonable period for security, debugging, and compliance purposes</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Deletion</h3>
                <p className="text-gray-600 mb-4">
                  Customers may request deletion of their account and associated data by contacting us at <a href="mailto:[SUPPORT_EMAIL]" className="text-brand hover:text-[#2a6bb0]">[SUPPORT_EMAIL]</a>. Upon account termination, we will delete or anonymize Customer data within a reasonable timeframe, except where retention is required by law or for legitimate business purposes (such as maintaining opt-out lists).
                </p>
                <p className="text-gray-600 mb-4">
                  End Users who wish to exercise data deletion rights should contact the Customer (Agency) that holds their information. We will cooperate with Customers to fulfill such requests.
                </p>
              </section>

              {/* 7. Security */}
              <section id="security" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">7. Security Measures</h2>
                <p className="text-gray-600 mb-4">
                  We implement reasonable administrative, technical, and physical safeguards designed to protect the information we process. These include:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li>Encryption of data in transit (TLS) and at rest</li>
                  <li>Access controls and authentication requirements for Platform users</li>
                  <li>Regular security assessments and monitoring</li>
                  <li>Incident response procedures</li>
                  <li>Employee access limited to those with a business need</li>
                  <li>Secure development practices</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  No method of transmission over the internet or electronic storage is completely secure. While we strive to protect personal information, we cannot guarantee absolute security.
                </p>
              </section>

              {/* 8. Your Rights */}
              <section id="your-rights" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">8. Your Rights</h2>
                <p className="text-gray-600 mb-4">
                  Depending on your jurisdiction, you may have the following rights regarding your personal information:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal information, subject to legal retention requirements</li>
                  <li><strong>Opt-out of SMS:</strong> Reply STOP to any message to opt out of further SMS communications from a Customer</li>
                  <li><strong>Data portability:</strong> Request your data in a structured, commonly used format</li>
                  <li><strong>Withdraw consent:</strong> Where processing is based on consent, withdraw that consent at any time</li>
                  <li><strong>Non-discrimination:</strong> Exercise your rights without receiving discriminatory treatment</li>
                </ul>

                <p className="text-gray-600 mb-4">
                  <strong>For Customers:</strong> Exercise your rights by contacting us at <a href="mailto:[SUPPORT_EMAIL]" className="text-brand hover:text-[#2a6bb0]">[SUPPORT_EMAIL]</a>.
                </p>
                <p className="text-gray-600 mb-4">
                  <strong>For End Users:</strong> Because Voley processes End User data on behalf of our Customers, End Users should direct privacy requests to the Agency that holds their information. We will cooperate with Customers to fulfill valid requests. If you are unable to reach the Agency, you may contact us and we will make reasonable efforts to direct your request appropriately.
                </p>
              </section>

              {/* 9. Agency Responsibilities */}
              <section id="agency-responsibilities" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">9. Agency / Customer Responsibilities</h2>
                <p className="text-gray-600 mb-4">
                  Our Customers are responsible for:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Lawful basis for processing:</strong> Ensuring they have a lawful basis (such as consent) to collect and share End User personal information with Voley for messaging purposes</li>
                  <li><strong>Privacy disclosures:</strong> Providing their own privacy notices to End Users that accurately describe how End User information will be processed, including through the use of Voley</li>
                  <li><strong>Consent collection:</strong> Obtaining and documenting valid consent from End Users before initiating SMS communications through the Platform</li>
                  <li><strong>Data accuracy:</strong> Ensuring the accuracy of contact information uploaded to the Platform</li>
                  <li><strong>End User requests:</strong> Responding to End User requests regarding their personal information and coordinating with Voley as needed to fulfill those requests</li>
                  <li><strong>Compliance with laws:</strong> Complying with all applicable privacy, telecommunications, and marketing laws in their jurisdiction, including TCPA, state privacy laws, and industry-specific regulations</li>
                </ul>
              </section>

              {/* 10. Children */}
              <section id="children" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">10. Children&apos;s Privacy</h2>
                <p className="text-gray-600 mb-4">
                  The Platform is not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we learn that we have collected personal information from a child under 16, we will take steps to delete that information promptly. If you believe a child has provided us with personal information, please contact us at <a href="mailto:[SUPPORT_EMAIL]" className="text-brand hover:text-[#2a6bb0]">[SUPPORT_EMAIL]</a>.
                </p>
              </section>

              {/* 11. Changes */}
              <section id="changes" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">11. Changes to This Privacy Policy</h2>
                <p className="text-gray-600 mb-4">
                  We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will post the updated policy on this page and update the &quot;Last Updated&quot; date. For material changes, we will provide notice through the Platform or by email to our Customers. Your continued use of the Platform after changes are posted constitutes acceptance of the updated policy.
                </p>
              </section>

              {/* 12. Contact */}
              <section id="contact" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">12. Contact Us</h2>
                <p className="text-gray-600 mb-4">
                  If you have questions about this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="bg-white border border-gray-200 rounded-2xl px-5 py-4 mb-6 [&>:first-child]:mt-0">
                  <p className="text-gray-700 mb-2"><strong>Voley</strong></p>
                  <p className="text-gray-600 mb-1">Email: <a href="mailto:[SUPPORT_EMAIL]" className="text-brand hover:text-[#2a6bb0]">[SUPPORT_EMAIL]</a></p>
                  <p className="text-gray-600 mb-1">Phone: [SUPPORT_PHONE]</p>
                  <p className="text-gray-600 mb-1">Address: [BUSINESS_ADDRESS]</p>
                  <p className="text-gray-600 mb-0">Website: <a href="https://letsvoley.com" className="text-brand hover:text-[#2a6bb0]">letsvoley.com</a></p>
                </div>
              </section>

              {/* Appendix A: CCPA */}
              <section id="ccpa" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Appendix A: California Consumer Privacy Act (CCPA) Supplement</h2>
                <p className="text-gray-600 mb-4">
                  This section applies to California residents and supplements the information in this Privacy Policy.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Categories of Personal Information Collected</h3>
                <p className="text-gray-600 mb-4">
                  In the preceding 12 months, we have collected the following categories of personal information:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Identifiers:</strong> Name, email address, phone number, IP address, account credentials</li>
                  <li><strong>Commercial information:</strong> Billing records, subscription history</li>
                  <li><strong>Internet or electronic network activity:</strong> Browser type, pages viewed, interaction data</li>
                  <li><strong>Professional or employment-related information:</strong> Business name, role, title (for Customer accounts)</li>
                  <li><strong>Inferences:</strong> Usage patterns and preferences derived from Platform activity</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Your California Rights</h3>
                <p className="text-gray-600 mb-4">
                  California residents have the right to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Know</strong> what personal information we collect, use, and disclose</li>
                  <li><strong>Delete</strong> personal information we hold about you (subject to exceptions)</li>
                  <li><strong>Opt out of the sale</strong> of personal information &mdash; we do not sell personal information</li>
                  <li><strong>Non-discrimination</strong> for exercising your CCPA rights</li>
                </ul>
                <p className="text-gray-600 mb-4">
                  To exercise these rights, contact us at <a href="mailto:[SUPPORT_EMAIL]" className="text-brand hover:text-[#2a6bb0]">[SUPPORT_EMAIL]</a>. We will verify your identity before processing requests.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Service Provider Status</h3>
                <p className="text-gray-600 mb-4">
                  Where we process End User personal information on behalf of our Customers, we do so as a &quot;Service Provider&quot; under the CCPA. We do not sell or share (for cross-context behavioral advertising) End User personal information.
                </p>
              </section>

              {/* Appendix B: GDPR */}
              <section id="gdpr" className="mb-12">
                <h2 className="text-2xl font-semibold mb-4 text-gray-900">Appendix B: General Data Protection Regulation (GDPR) Supplement</h2>
                <p className="text-gray-600 mb-4">
                  This section applies to individuals in the European Economic Area (EEA), United Kingdom, or Switzerland and supplements the information in this Privacy Policy.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Legal Bases for Processing</h3>
                <p className="text-gray-600 mb-4">
                  We process personal data on the following legal bases:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Performance of a contract:</strong> To provide the Platform and fulfill our obligations to Customers</li>
                  <li><strong>Legitimate interests:</strong> To operate, improve, and secure the Platform, and to communicate with Customers about their accounts</li>
                  <li><strong>Legal obligations:</strong> To comply with applicable laws and regulations</li>
                  <li><strong>Consent:</strong> Where specifically required, such as for certain cookie usage</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Your GDPR Rights</h3>
                <p className="text-gray-600 mb-4">
                  In addition to the rights described in Section 8, EEA/UK/Swiss individuals have the right to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 mb-6 space-y-2">
                  <li><strong>Restrict processing</strong> of your personal data in certain circumstances</li>
                  <li><strong>Object to processing</strong> based on legitimate interests</li>
                  <li><strong>Lodge a complaint</strong> with your local data protection authority</li>
                </ul>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">International Transfers</h3>
                <p className="text-gray-600 mb-4">
                  Voley is based in the United States. If you access the Platform from outside the United States, your information will be transferred to and processed in the United States. We implement appropriate safeguards for international data transfers, including standard contractual clauses where applicable.
                </p>

                <h3 className="text-xl font-semibold mt-6 mb-3 text-gray-900">Data Processor Role</h3>
                <p className="text-gray-600 mb-4">
                  Where we process End User personal data on behalf of our Customers, we act as a Data Processor. Our Customers are the Data Controllers. We process personal data only in accordance with our Customers&apos; documented instructions and our Data Processing Agreement.
                </p>
              </section>

            </div>

            {/* Footer Navigation */}
            <div className="mt-16 pt-8 border-t border-gray-200">
              <div className="flex justify-between items-center">
                <Link
                  href="/docs"
                  className="inline-flex items-center gap-2 px-5 py-2.5 border border-gray-200 text-sm font-medium rounded-full text-gray-700 bg-white hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm transition-all group"
                >
                  <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
                  Back to Docs
                </Link>
                <div className="text-sm text-gray-500">
                  See also: <Link href="/docs/terms" className="text-brand hover:text-[#2a6bb0] font-medium">Terms & Conditions</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
